import { ChangeEvent, useMemo, useState } from "react";
import cn from "classnames";
import { Button, BUTTON_VARIANT } from "components/UI/Button";
import { ColorPicker } from "components/UI/ColorPicker";
import { CustomSelect, OptionType } from "components/UI/CustomSelect";
import { Cog, Pallete, Trash } from "components/UI/Icons";
import { Input, INPUT_VARIANT } from "components/UI/Input";
import { Modal } from "components/UI/Modal";
import { DEFAULT_PALLET_OPTIONS, PALLET_TYPE } from "constants/palletOptions.constants";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { InputNumber, PalletRow } from "models/packingList";
import {
  getInvalidRequiredPalletFields,
  isFilledRequiredNumber,
  removeCustomPallet,
  selectCustomPallets,
  selectOptions,
  selectPalletRowByGroupId,
  selectShowCargoValidation,
  updatePalletRow,
} from "store";
import { parseInputNumber } from "utils/parseInputNumber.utils";
import { fromDisplayLength, fromDisplayWeight, toDisplayLength, toDisplayWeight } from "utils/units.utils";
import { PalletSave } from "./PalletSave";
import s from "./PalletTable.module.scss";

type NumberField = keyof Pick<PalletRow, "length" | "width" | "height" | "depth" | "maxWeight">;

const EMPTY_PLACEHOLDER = "0";

const toVisibleNumber = (value: InputNumber): string | number => (isFilledRequiredNumber(value) ? value! : "");

export const PalleteRow = ({ groupId }: { groupId: string }) => {
  const dispatch = useAppDispatch();
  const row = useAppSelector(selectPalletRowByGroupId(groupId));
  const customPallets = useAppSelector(selectCustomPallets);
  const { lengthUnits, weightUnits } = useAppSelector(selectOptions);
  const showValidation = useAppSelector(selectShowCargoValidation);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const customPalletOptions: OptionType[] = useMemo(
    () =>
      customPallets.map((pallet) => ({
        value: pallet.id,
        label: pallet.palleteName ?? "",
        valueIcon: <Pallete />,
        action: (
          <Button
            variant={BUTTON_VARIANT.ONLY_ICON_TRANSPARENT}
            className={s.optionDelBtn}
            onClick={() => dispatch(removeCustomPallet(pallet.id))}
          >
            <Trash />
          </Button>
        ),
      })),
    [customPallets, dispatch]
  );

  const palleteOptions = useMemo(() => [...DEFAULT_PALLET_OPTIONS, ...customPalletOptions], [customPalletOptions]);

  if (!row) return null;

  const palleteType = palleteOptions.find((option) => option.value === row.palletType) ?? DEFAULT_PALLET_OPTIONS[0];
  const savedCustom = customPallets.find((pallet) => pallet.id === row.palletType);
  const isCustomPallet = row.palletType === PALLET_TYPE.CUSTOM_PALLETS || Boolean(savedCustom);
  const disableTd = !isCustomPallet;
  const invalidFields = showValidation ? new Set(getInvalidRequiredPalletFields(row)) : null;
  const fieldError = (field: NumberField) => Boolean(invalidFields?.has(field));
  const numberInputClass = (field: NumberField) => cn(s.numberInput, fieldError(field) && s.placeholderError);

  const handleChange = (field: NumberField) => (e: ChangeEvent<HTMLInputElement>) => {
    const displayValue = parseInputNumber(e.target.value);
    const value =
      field === "maxWeight"
        ? fromDisplayWeight(displayValue, weightUnits)
        : fromDisplayLength(displayValue, lengthUnits);
    dispatch(updatePalletRow({ groupId, [field]: value }));
  };

  return (
    <>
      <div className={s.row}>
        <div className={s.cell}>
          <CustomSelect<OptionType>
            className={s.select}
            componentsClassNames={{
              menu: s.menu,
            }}
            options={palleteOptions}
            value={palleteType}
            handleChange={(option) => dispatch(updatePalletRow({ groupId, palletType: option.value }))}
          />
        </div>
        <div className={cn(s.cell, { [s.disable]: disableTd })}>
          <Input
            type="number"
            variant={INPUT_VARIANT.GHOST}
            disabled={disableTd}
            inputClassName={numberInputClass("length")}
            placeholder={EMPTY_PLACEHOLDER}
            value={toVisibleNumber(toDisplayLength(row.length, lengthUnits))}
            onChange={handleChange("length")}
          />
        </div>
        <div className={cn(s.cell, { [s.disable]: disableTd })}>
          <Input
            type="number"
            variant={INPUT_VARIANT.GHOST}
            disabled={disableTd}
            inputClassName={numberInputClass("width")}
            placeholder={EMPTY_PLACEHOLDER}
            value={toVisibleNumber(toDisplayLength(row.width, lengthUnits))}
            onChange={handleChange("width")}
          />
        </div>
        <div className={cn(s.cell, { [s.disable]: disableTd })}>
          <Input
            type="number"
            variant={INPUT_VARIANT.GHOST}
            disabled={disableTd}
            inputClassName={numberInputClass("height")}
            placeholder={EMPTY_PLACEHOLDER}
            value={toVisibleNumber(toDisplayLength(row.height, lengthUnits))}
            onChange={handleChange("height")}
          />
        </div>
        <div className={cn(s.cell, { [s.disable]: disableTd })}>
          <Input
            type="number"
            variant={INPUT_VARIANT.GHOST}
            disabled={disableTd}
            inputClassName={numberInputClass("depth")}
            placeholder={EMPTY_PLACEHOLDER}
            value={toVisibleNumber(toDisplayLength(row.depth, lengthUnits))}
            onChange={handleChange("depth")}
          />
        </div>
        <div className={cn(s.cell, { [s.disable]: disableTd })}>
          <Input
            type="number"
            variant={INPUT_VARIANT.GHOST}
            disabled={disableTd}
            inputClassName={numberInputClass("maxWeight")}
            placeholder={EMPTY_PLACEHOLDER}
            value={toVisibleNumber(toDisplayWeight(row.maxWeight, weightUnits))}
            onChange={handleChange("maxWeight")}
          />
        </div>
        <div className={cn(s.cell, s.tdColor)}>
          <ColorPicker value={row.color} onChange={(color) => dispatch(updatePalletRow({ groupId, color }))} />
        </div>
        <div className={s.cell}>
          <div className={s.actionsBox}>
            {isCustomPallet && (
              <Button variant={BUTTON_VARIANT.ONLY_ICON_TRANSPARENT} onClick={() => setIsModalOpen(true)}>
                <Cog />
              </Button>
            )}
          </div>
        </div>
      </div>

      <Modal isOpen={isModalOpen} closeButton={true} onClose={() => setIsModalOpen(false)}>
        <PalletSave onClose={() => setIsModalOpen(false)} groupId={groupId} row={row} editPalletId={savedCustom?.id} />
      </Modal>
    </>
  );
};
